<script setup lang="ts">
import type { SectionBaseItem } from '~/models/Content/SectionBaseItem'
import type { HomeAdult } from '~/models/Content/HomeAdult'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { sortByNameGradeSubject } from '~/utils/productSorter'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName } from '~/models/Content/Color'
import useSlugify from '~/composables/useSlugify'
import useSections from '~/composables/useSections'
import useProductHelper from '~/composables/useProductHelper'
import useHome from '~/composables/useHome'
import { useAppColor } from '~/composables/useAppColor'
import Button from '~/components/ui/Button.vue'
import SectionsSkeleton from '~/components/skeletons/SectionsSkeleton.vue'
import SectionRenderer from '~/components/ResourceEngine/SectionRenderer.vue'
import DefaultMessages from '~/components/home/DefaultMessages.vue'
import DefaultIllustration from '~/components/home/DefaultIllustration.vue'
import DefaultGreetings from '~/components/home/DefaultGreetings.vue'

useAppColor(ColorName.White)

const { t } = useI18n()
const { products } = storeToRefs(useProductStore())
const { slugify } = useSlugify()
const { user } = storeToRefs(useAuthStore())
const { isProductAdult } = useProductHelper()

const filteredProducts = computed(() => products.value
  .filter(isProductAdult)
  .sort(sortByNameGradeSubject))

const { data, isLoading } = useHome<HomeAdult>(ContentType.HomeAdult)

const sectionIds = computed(() => data?.value?.sections?.destinationContentIds ?? [])

const { data: sections, isLoading: isLoadingSections } = useSections<SectionBaseItem>(sectionIds, [
  ContentType.SectionStandalone,
  ContentType.SectionBox,
  ContentType.SectionExpanded,
])
</script>

<template>
  <header class="bg-green-40 relative mb-12 flex-col justify-center text-white">
    <div class="max-w-screen-au xs:pb-14 relative z-20 mx-auto size-full px-4 pt-40 pb-10 transition-all sm:px-8 sm:pt-28">
      <h1
        class="sr-only"
        v-text="t('home.title')"
      />
      <DefaultMessages class="mb-6 !px-0" />
      <DefaultGreetings
        v-if="user"
        :name="user.fullName"
      />
      <p
        v-if="filteredProducts.length"
        class="mt-4 mb-2 font-medium"
        v-text="t('home.yourProducts')"
      />
      <div
        v-if="filteredProducts.length"
        class="xs:pb-0 md:max-w-[70%]"
      >
        <ul class="xs:flex-wrap xs:overflow-x-visible relative z-10 -mx-4 flex h-fit gap-3 overflow-x-scroll px-4 pb-4">
          <li
            v-for="product in filteredProducts"
            :key="product.ean"
          >
            <Button
              :to="{ name: 'product',
                     params: {
                       locationId: product.aunivers.locationId,
                       slug: slugify(product.aunivers.name),
                     },
              }"
              color="white"
              size="lg"
            >
              {{ product.aunivers.name }}
            </Button>
          </li>
        </ul>
        <DefaultIllustration />
      </div>
    </div>
  </header>
  <section
    v-if="isLoading || isLoadingSections"
    class="max-w-screen-au mx-auto"
  >
    <SectionsSkeleton />
  </section>

  <SectionRenderer
    v-else
    :items="sections ?? []"
  />
</template>
